// react
import {
	ChangeEvent,
	Dispatch,
	FormEvent,
	SetStateAction,
	useContext,
	useRef,
	useState,
} from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner, ContextUser } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';
import { ContextSpendPlan } from '../../pages/ProjectV2/ProjectV2.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';
import { useScrollToError } from '../../../hooks/useScrollToError';

// mui
import {
	Alert,
	alpha,
	Box,
	Checkbox,
	Chip,
	Divider,
	FormControl,
	FormHelperText,
	Grid,
	InputAdornment,
	InputLabel,
	LinearProgress,
	MenuItem,
	Paper,
	Select,
	SelectChangeEvent,
	Stack,
	TextField,
	Typography,
	useTheme,
} from '@mui/material';

// icons
import {
	AccountTree as IconActivity,
	Business as IconEntity,
	CalendarMonth as IconCalendar,
	GppGood as IconCondition,
	Assignment as IconWorkPlan,
	Description as IconContract,
} from '@mui/icons-material';

// types
import {
	Category,
	Component,
	Contract,
	Entity,
	ExpenseType,
	PlanningActivityPayload,
	PlanningActivity,
} from '../../../@types';

// utils
import { formatCurrency } from '../../../utils/currency';
import { formatDate } from '../../../utils/dates';
import {
	getV1ContractAmountForCurrency,
	getV1ContractSupplierName,
} from '../../pages/ProjectV2/Contracts/Contracts.utils';

// components
import {
	TimelinePresets,
	parseFiscalYearStartMonth,
} from '../../ui/TimelinePresets/TimelinePresets';
import {
	CustomFieldsFormSection,
	validateCustomFields,
} from '../../ui/CustomFieldsFormSection/CustomFieldsFormSection';

// Props & Types

// Re-export V2ActivityPayload as ActivityPayload for backward compatibility
export type ActivityPayload = PlanningActivityPayload;

export interface FormAddActivityValues {
	amountLocal: number | '';
	categoryId: number | '';
	commitmentAmount: number | '';
	commitmentAmountLocal: number | '';
	componentId: number | '';
	contractId: number | '';
	currency: string;
	customData: Record<string, unknown>;
	description: string;
	endDate: string;
	workPlanId: number | '';
	expenseTypeId: number | '';
	name: string;
	parentActivityId: number | '';
	plannedAmount: number | '';
	responsibleEntityId: number | '';
	startDate: string;
	timelinePreset: string;
}

export interface FormAddActivityProps {
	activity?: PlanningActivity | null;
	onClose?: () => void;
	onSuccess?: () => void;
	parentActivityId?: number;
	preSelectedCategoryId?: number;
	preSelectedComponentId?: number;
	preSelectedWorkPlanId?: number;
	setIsLoading?: Dispatch<SetStateAction<boolean>>;
}

// Component

export const FormAddActivity = ({
	activity,
	onClose,
	onSuccess,
	parentActivityId,
	preSelectedCategoryId,
	preSelectedComponentId,
	preSelectedWorkPlanId,
	setIsLoading,
}: FormAddActivityProps) => {
	// hooks
	const theme = useTheme();
	const { t } = useTranslation();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);
	const { user } = useContext(ContextUser);
	const {
		activities,
		conditions,
		contracts,
		createActivity,
		createContract,
		workPlans,
		updateActivity,
		updateContract,
	} = useContext(ContextSpendPlan);

	// derived
	const isEditMode = Boolean(activity);
	const formId = isEditMode ? 'form-edit-activity' : 'form-add-activity';
	const reportingCurrency =
		project?.currencies?.find((c) => c.type === 'REPORTING')?.currency || 'USD';
	const localCurrency = project?.currencies?.find((c) => c.type === 'LOCAL')?.currency || null;

	// existing contract mapping (if editing)
	const existingContractForActivity = activity
		? contracts.find((c) => c.planningActivity === activity.id)
		: null;

	// Helper to get work plan ID from activity
	const getWorkPlanIdFromActivity = (act: PlanningActivity): number | '' => {
		if (typeof act.workPlan === 'number') return act.workPlan;
		if (act.workPlan && typeof act.workPlan === 'object') return act.workPlan.id;
		return '';
	};

	// Helper to get entity ID from activity
	const getEntityIdFromActivity = (act: PlanningActivity): number | '' => {
		if (typeof act.entity === 'number') return act.entity;
		if (act.entity && typeof act.entity === 'object') return act.entity.id;
		return '';
	};

	// Helper to get parent activity ID from activity
	const getParentActivityIdFromActivity = (act: PlanningActivity): number | '' => {
		if (typeof act.parent === 'number') return act.parent;
		return '';
	};

	// Helper to get category ID from activity
	const getCategoryIdFromActivity = (act: PlanningActivity): number | '' => {
		if (typeof act.category === 'number') return act.category;
		if (act.category && typeof act.category === 'object') return act.category.id;
		return '';
	};

	// Helper to get component ID from activity
	const getComponentIdFromActivity = (act: PlanningActivity): number | '' => {
		if (typeof act.component === 'number') return act.component;
		if (act.component && typeof act.component === 'object') return act.component.id;
		return '';
	};

	// Helper to get expense type ID from activity
	const getExpenseTypeIdFromActivity = (act: PlanningActivity): number | '' => {
		if (typeof act.expenseType === 'number') return act.expenseType;
		if (act.expenseType && typeof act.expenseType === 'object') return act.expenseType.id;
		return '';
	};

	// Helper to format date for HTML date input (YYYY-MM-DD format)
	const formatDateForInput = (dateValue: string | null | undefined): string => {
		if (!dateValue) return '';
		return formatDate({ date: dateValue, format: 'YYYY-MM-DD' });
	};

	// State for showing timeline section - initialize to true if editing activity with dates
	const [showTimeline, setShowTimeline] = useState(() => {
		return Boolean(activity?.dateStart || activity?.dateEnd);
	});

	// state - form values
	const [values, setValues] = useState<FormAddActivityValues>(() => {
		if (activity) {
			return {
				amountLocal: activity.amountLocal || '',
				categoryId: getCategoryIdFromActivity(activity),
				commitmentAmount: existingContractForActivity?.commitmentAmount ?? '',
				commitmentAmountLocal: existingContractForActivity?.commitmentAmountLocal ?? '',
				componentId: getComponentIdFromActivity(activity),
				contractId: existingContractForActivity?.id || '',
				currency: activity.currency || 'USD',
				customData: activity.customData || {},
				description: activity.description || '',
				endDate: formatDateForInput(activity.dateEnd),
				workPlanId: getWorkPlanIdFromActivity(activity),
				expenseTypeId: getExpenseTypeIdFromActivity(activity),
				name: activity.name || '',
				parentActivityId: getParentActivityIdFromActivity(activity),
				plannedAmount: activity.amount || '',
				responsibleEntityId: getEntityIdFromActivity(activity),
				startDate: formatDateForInput(activity.dateStart),
				timelinePreset: activity.dateStart ? 'custom' : '',
			};
		}
		return {
			amountLocal: '',
			categoryId: preSelectedCategoryId || '',
			commitmentAmount: '',
			commitmentAmountLocal: '',
			componentId: preSelectedComponentId || '',
			contractId: '',
			currency: 'USD',
			customData: {},
			description: '',
			endDate: '',
			workPlanId: preSelectedWorkPlanId || '',
			expenseTypeId: '',
			name: '',
			parentActivityId: parentActivityId || '',
			plannedAmount: '',
			responsibleEntityId: '',
			startDate: '',
			timelinePreset: '',
		};
	});

	// state - validation errors
	const [errors, setErrors] = useState<
		Partial<Record<keyof FormAddActivityValues | 'currency', string>>
	>({});

	// state - submission error
	const [error, setError] = useState<string | null>(null);

	// scroll to error when it occurs
	const errorRef = useScrollToError(error);

	// state - data
	const [categories, setCategories] = useState<Category[]>([]);
	const [components, setComponents] = useState<Component[]>([]);
	const [entities, setEntities] = useState<Entity[]>([]);
	const [expenseTypes, setExpenseTypes] = useState<ExpenseType[]>([]);
	const [v1Contracts, setV1Contracts] = useState<Contract[]>([]);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// fetch categories
	const { isLoading: isLoadingCategories } = useFetch({
		isEnabled: Boolean(project?.id),
		onSuccess: (res) => {
			if (res.data) {
				setCategories(res.data);
			}
		},
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		url: `${process.env.REACT_APP_API_URL}/categories?project=${project?.id}&fields=id,name,status&status=ACTIVE`,
	});

	// fetch components
	const { isLoading: isLoadingComponents } = useFetch({
		isEnabled: Boolean(project?.id),
		onSuccess: (res) => {
			if (res.data) {
				setComponents(res.data);
			}
		},
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		url: `${process.env.REACT_APP_API_URL}/components?project=${project?.id}&fields=id,name,status&status=ACTIVE`,
	});

	// fetch entities - only PIU and SUB_PIU types (not suppliers/beneficiaries)
	const { isLoading: isLoadingEntities } = useFetch({
		isEnabled: Boolean(project?.id),
		onSuccess: (res) => {
			if (res.data) {
				// ABAC: filter entities to user's scope (WBG users see all)
				const filtered =
					!user?.isWbg && user?.abac?.entities?.length
						? res.data.filter((e: Entity) => user.abac.entities.includes(e.id))
						: res.data;
				setEntities(filtered);
			}
		},
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		url: `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&fields=id,name,type&type=PIU&type=SUB_PIU`,
	});

	// fetch expense types
	const { isLoading: isLoadingExpenseTypes } = useFetch({
		isEnabled: Boolean(project?.id),
		onSuccess: (res) => {
			if (res.data) {
				setExpenseTypes(res.data);
			}
		},
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		url: `${process.env.REACT_APP_API_URL}/expense-types?project=${project?.id}&fields=id,name,status&status=ACTIVE&orderBy=name&order=ASC`,
	});

	// fetch V1 contracts for this project
	const { isLoading: isLoadingContracts } = useFetch({
		isEnabled: Boolean(project?.id),
		onSuccess: (res) => {
			if (res.data) {
				setV1Contracts(res.data);
			}
		},
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		url: `${process.env.REACT_APP_API_URL}/contracts?project=${project?.id}&populate=supplier`,
	});

	// derived - selected work plan
	const selectedWorkPlan = values.workPlanId
		? workPlans.find((e) => e.id === values.workPlanId)
		: null;

	// Helper to compute available amount for an work plan
	const getWorkPlanAvailableAmount = (workPlan: (typeof workPlans)[0]): number => {
		// Get all root-level activities for this work plan (activities with no parent)
		const rootLevelActivities = activities.filter((a) => {
			const actWorkPlanId = typeof a.workPlan === 'number' ? a.workPlan : a.workPlan?.id;
			// Check if activity belongs to this work plan and has no parent
			const actParentId = typeof a.parent === 'number' ? a.parent : null;
			return actWorkPlanId === workPlan.id && !actParentId;
		});

		// Sum of root-level activity amounts, excluding the current activity if editing
		const currentActivityId = activity?.id;
		const activitiesForSum = rootLevelActivities.filter((a) => a.id !== currentActivityId);
		const allocatedAmount = activitiesForSum.reduce((sum, a) => sum + (a.amount || 0), 0);

		// Calculate available amount
		const availableAmount = (workPlan.amount || 0) - allocatedAmount;

		return Math.max(0, availableAmount);
	};

	// Helper to compute available amount for a parent activity
	const getParentActivityAvailableAmount = (parentId: number): number => {
		const parent = activities.find((a) => a.id === parentId);
		if (!parent) return 0;

		// Get all sub-activities of this parent
		const subActivities = activities.filter((a) => {
			const actParentId = typeof a.parent === 'number' ? a.parent : null;
			return actParentId === parentId;
		});

		// Sum of sub-activity amounts (excluding current activity if editing)
		const subActivityTotal = subActivities
			.filter((a) => !activity || a.id !== activity.id)
			.reduce((sum, a) => sum + (a.amount || 0), 0);

		return Math.max(0, (parent.amount || 0) - subActivityTotal);
	};

	// derived - parent activity (if adding sub-activity)
	const parentActivity = parentActivityId
		? activities.find((a) => a.id === parentActivityId)
		: null;

	// Helper to get work plan base/prerequisite conditions (exclude contract-linked payment conditions)
	const getWorkPlanConditions = (workPlan: (typeof workPlans)[0]) => {
		return conditions.filter((c) => {
			const condWorkPlanId = typeof c.workPlan === 'number' ? c.workPlan : c.workPlan?.id;
			const condContractId = typeof c.contract === 'number' ? c.contract : c.contract?.id;
			return condWorkPlanId === workPlan.id && !condContractId;
		});
	};

	// handlers
	const handleChange = (
		e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<number | '' | string>
	) => {
		const { name, value } = e.target;
		setValues((prev) => ({
			...prev,
			[name]: value,
		}));
		// Clear error when field is changed
		if (errors[name as keyof FormAddActivityValues]) {
			setErrors((prev) => ({ ...prev, [name]: undefined }));
		}
	};

	// handler for custom column field changes
	const handleCustomFieldChange = (columnId: string, value: unknown) => {
		setValues((prev) => ({
			...prev,
			customData: {
				...prev.customData,
				[columnId]: value,
			},
		}));
	};

	// Synchronous in-flight guard. Prevents duplicate POSTs when a user
	// double-clicks the submit button before React flushes `setIsLoading(true)`
	// (the LoadingButton `loading` prop only disables the button after a
	// re-render). Without this, two `createActivity()` calls can race through
	// and create two activities — see QA bug 2026-05-28 #18.
	const isSubmittingRef = useRef(false);

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (isSubmittingRef.current) {
			return;
		}
		isSubmittingRef.current = true;
		setError(null);
		setErrors({}); // Clear all field errors before validation

		// Validate
		const newErrors: Partial<Record<keyof FormAddActivityValues, string>> = {};

		if (!values.name.trim()) {
			newErrors.name = 'Activity name is required';
		}
		if (!values.responsibleEntityId) {
			newErrors.responsibleEntityId = 'Please select a responsible entity';
		}
		if (!values.plannedAmount || Number(values.plannedAmount) <= 0) {
			newErrors.plannedAmount = 'Please enter a valid amount';
		}
		// Date validation only if dates are provided
		if (values.startDate && values.endDate && values.startDate > values.endDate) {
			newErrors.endDate = 'End date must be after start date';
		}

		// Check if amount exceeds availability (parent activity for sub-activities, work plan otherwise)
		if (values.plannedAmount) {
			const effectiveParentId = parentActivityId || values.parentActivityId;

			if (effectiveParentId) {
				// Sub-activity: validate against parent activity's available amount
				const availableAmount = getParentActivityAvailableAmount(effectiveParentId as number);
				if (Number(values.plannedAmount) > availableAmount) {
					newErrors.plannedAmount = `Amount exceeds parent activity's available funds (${formatCurrency(
						{
							currency: parentActivity?.currency || selectedWorkPlan?.currency || 'USD',
							value: availableAmount,
						}
					)})`;
				}
			} else if (selectedWorkPlan) {
				// Top-level activity: validate against work plan's available amount
				const availableAmount = getWorkPlanAvailableAmount(selectedWorkPlan);
				if (Number(values.plannedAmount) > availableAmount) {
					newErrors.plannedAmount = `Amount exceeds available work plan funds (${formatCurrency({
						currency: selectedWorkPlan.currency,
						value: availableAmount,
					})})`;
				}
			}
		}

		// Validate required custom columns
		const customColumnErrors = validateCustomFields(
			(project?.customFieldDefinitions || []).find((d) => d.entityType === 'activity')
				?.definitions || [],
			values.customData
		);

		if (customColumnErrors.length > 0) {
			setError(`Required fields missing: ${customColumnErrors.join(', ')}`);
			isSubmittingRef.current = false;
			return;
		}

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors);
			isSubmittingRef.current = false;
			return;
		}

		try {
			if (setIsLoading) {
				setIsLoading(true);
			}

			// Build payload
			const payload: PlanningActivityPayload = {
				amount: Number(values.plannedAmount),
				amountLocal: values.amountLocal ? Number(values.amountLocal) : null,
				categoryId: values.categoryId ? Number(values.categoryId) : null,
				componentId: values.componentId ? Number(values.componentId) : null,
				currency: reportingCurrency,
				currencyLocal: localCurrency || null,
				customData: values.customData,
				dateEnd: values.endDate || null,
				dateStart: values.startDate || null,
				description: values.description || null,
				entity: values.responsibleEntityId as number,
				workPlan: values.workPlanId ? (values.workPlanId as number) : null,
				expenseTypeId: values.expenseTypeId ? Number(values.expenseTypeId) : null,
				name: values.name,
				parentActivityId: values.parentActivityId ? (values.parentActivityId as number) : null,
				projectId: project?.id ?? null,
				status: isEditMode && activity ? activity.status : 'PLANNED',
			};

			let res;
			let activityId: number;

			if (isEditMode && activity) {
				// PATCH
				res = await updateActivity(activity.id, payload);
				activityId = activity.id;
			} else {
				// POST
				res = await createActivity(payload);
				activityId = res.data?.id || 0;
			}

			if (res.error) {
				throw new Error(res.error.message);
			}

			// Handle contract linkage (V2 contract mapping)
			const selectedContractId = values.contractId ? Number(values.contractId) : null;
			const existingV2Contract = existingContractForActivity;

			const commitmentAmt = values.commitmentAmount !== '' ? Number(values.commitmentAmount) : null;
			const commitmentAmtLocal =
				values.commitmentAmountLocal !== '' ? Number(values.commitmentAmountLocal) : null;

			if (selectedContractId && activityId) {
				// User selected a contract - create or update the V2 contract linkage
				if (existingV2Contract) {
					// Update existing V2 contract if the contract or commitment changed
					const contractChanged = existingV2Contract.id !== selectedContractId;
					const commitmentChanged = (existingV2Contract.commitmentAmount ?? null) !== commitmentAmt;
					const commitmentLocalChanged =
						(existingV2Contract.commitmentAmountLocal ?? null) !== commitmentAmtLocal;
					if (contractChanged || commitmentChanged || commitmentLocalChanged) {
						const updateRes = await updateContract(existingV2Contract.id, {
							contractId: selectedContractId,
							commitmentAmount: commitmentAmt,
							commitmentAmountLocal: commitmentAmtLocal,
							projectId: project?.id ?? null,
						});
						if (updateRes.error) {
							throw new Error(updateRes.error.message || 'Failed to update contract link');
						}
					}
				} else {
					// Create new V2 contract linkage
					const createRes = await createContract({
						activityId,
						contractId: selectedContractId,
						commitmentAmount: commitmentAmt,
						commitmentAmountLocal: commitmentAmtLocal,
						projectId: project?.id ?? null,
					});
					if (createRes.error) {
						throw new Error(createRes.error.message || 'Failed to link contract');
					}
				}
			} else if (!selectedContractId && existingV2Contract) {
				// User cleared the contract selection from the activity editor.
				// Unlink the contract from this activity without changing its
				// status — the contract row stays put and can be re-linked to
				// another activity. Status flipping (e.g. DEACTIVATED) is a
				// contract-side action and is not the activity editor's job.
				const unlinkRes = await updateContract(existingV2Contract.id, {
					activityId: null,
					commitmentAmount: null,
					commitmentAmountLocal: null,
				});
				if (unlinkRes.error) {
					throw new Error(unlinkRes.error.message || 'Failed to remove contract link');
				}
			}

			if (onSuccess) {
				onSuccess();
			}

			setBanner({
				hasClose: true,
				message: `Activity "${values.name}" ${isEditMode ? 'updated' : 'created'} successfully`,
				severity: 'success',
			});

			if (onClose) {
				onClose();
			}
		} catch (err) {
			setError(
				err instanceof Error
					? err.message
					: `Failed to ${isEditMode ? 'update' : 'create'} activity`
			);
		} finally {
			if (setIsLoading) {
				setIsLoading(false);
			}
			isSubmittingRef.current = false;
		}
	};

	const isLoading =
		isLoadingCategories ||
		isLoadingComponents ||
		isLoadingContracts ||
		isLoadingEntities ||
		isLoadingExpenseTypes;

	return (
		<form id={formId} noValidate onSubmit={handleSubmit}>
			{isLoading && <LinearProgress sx={{ mb: 2 }} />}

			{/* Parent Activity Indicator */}
			{parentActivityId && !isEditMode && (
				<Alert severity="info" sx={{ mb: 2 }}>
					Creating sub-activity under:{' '}
					<strong>
						{activities.find((a) => a.id === parentActivityId)?.name ||
							`Activity #${parentActivityId}`}
					</strong>
				</Alert>
			)}

			<Grid container spacing={3}>
				{/* Activity Name */}
				<Grid item xs={12}>
					<TextField
						autoFocus
						error={Boolean(errors.name)}
						fullWidth
						helperText={errors.name}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<IconActivity color="action" />
								</InputAdornment>
							),
						}}
						label={t('spendPlan.activities.activityName')}
						name="name"
						onChange={handleChange}
						placeholder={t('spendPlan.activities.activityNamePlaceholder')}
						required
						value={values.name}
					/>
				</Grid>

				{/* Description */}
				<Grid item xs={12}>
					<TextField
						fullWidth
						label={t('description')}
						multiline
						name="description"
						onChange={handleChange}
						placeholder={t('spendPlan.activities.descriptionPlaceholder')}
						rows={2}
						value={values.description}
					/>
				</Grid>

				{/* Work Plan Selection */}
				<Grid item xs={12}>
					<FormControl error={Boolean(errors.workPlanId)} fullWidth>
						<InputLabel>Work Plan</InputLabel>
						<Select
							disabled={Boolean(preSelectedWorkPlanId)}
							label={t('workPlan')}
							name="workPlanId"
							onChange={(e) => {
								const newWorkPlanId = e.target.value;
								handleChange(e);
								// Auto-set currency from work plan when selected
								if (newWorkPlanId) {
									const workPlan = workPlans.find((env) => env.id === newWorkPlanId);
									if (workPlan?.currency) {
										setValues((prev) => ({ ...prev, currency: workPlan.currency }));
									}
								}
							}}
							startAdornment={
								<InputAdornment position="start">
									<IconWorkPlan color="action" />
								</InputAdornment>
							}
							value={values.workPlanId}>
							<MenuItem value="">
								<Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
									None
								</Typography>
							</MenuItem>
							{workPlans.map((workPlan) => (
								<MenuItem key={workPlan.id} value={workPlan.id}>
									<Stack
										direction="row"
										alignItems="center"
										justifyContent="space-between"
										sx={{ width: '100%' }}>
										<Box>
											<Typography variant="body2">{workPlan.name}</Typography>
											{workPlan.fiscalYear && (
												<Typography variant="body2" color="text.secondary">
													{workPlan.fiscalYear}
												</Typography>
											)}
										</Box>
										<Chip
											label={formatCurrency({
												currency: workPlan.currency,
												value: getWorkPlanAvailableAmount(workPlan),
											})}
											size="small"
											color="success"
											variant="outlined"
										/>
									</Stack>
								</MenuItem>
							))}
						</Select>
						{errors.workPlanId && <FormHelperText>{errors.workPlanId}</FormHelperText>}
					</FormControl>
				</Grid>

				{/* Show available funds info - parent activity for sub-activities, work plan otherwise */}
				{(parentActivityId || selectedWorkPlan) && (
					<Grid item xs={12}>
						<Paper
							elevation={0}
							sx={{
								bgcolor: 'grey.50',
								border: '1px solid',
								borderColor: 'divider',
								borderRadius: 2,
								p: 2,
							}}>
							<Stack direction="row" justifyContent="space-between" alignItems="center">
								<Box>
									<Typography variant="body2" color="text.secondary">
										{parentActivityId ? 'Available in Parent Activity' : 'Available in Work Plan'}
									</Typography>
									<Typography variant="h6" sx={{ fontWeight: 600 }}>
										{formatCurrency({
											currency: parentActivity?.currency || selectedWorkPlan?.currency || 'USD',
											value: parentActivityId
												? getParentActivityAvailableAmount(parentActivityId)
												: selectedWorkPlan
													? getWorkPlanAvailableAmount(selectedWorkPlan)
													: 0,
										})}
									</Typography>
								</Box>
							</Stack>
							{selectedWorkPlan && getWorkPlanConditions(selectedWorkPlan).length > 0 && (
								<Box sx={{ mt: 2 }}>
									<Divider sx={{ mb: 2 }} />
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{ mb: 0.5, fontWeight: 600 }}>
										Work Plan Requirements ({getWorkPlanConditions(selectedWorkPlan).length})
									</Typography>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ display: 'block', mb: 1.5 }}>
										These conditions are defined at the work plan level and may apply to activities
										within this work plan.
									</Typography>
									<Stack spacing={1}>
										{getWorkPlanConditions(selectedWorkPlan).map((condition) => {
											return (
												<Stack
													key={condition.id}
													direction="row"
													alignItems="center"
													spacing={1}
													sx={{
														bgcolor: alpha(theme.palette.grey[500], 0.06),
														borderRadius: 1,
														px: 1.5,
														py: 1,
													}}>
													<IconCondition
														sx={{
															color: 'grey.500',
															fontSize: 20,
														}}
													/>
													<Box sx={{ flex: 1 }}>
														<Typography variant="body2" sx={{ fontWeight: 500 }}>
															{condition.name}
														</Typography>
														{condition.description && (
															<Typography variant="body2" color="text.secondary">
																{condition.description}
															</Typography>
														)}
													</Box>
													{condition.isRequired && (
														<Chip
															label={t('required')}
															size="small"
															variant="outlined"
															sx={{
																borderColor: theme.palette.warning.main,
																color: 'warning.dark',
																fontWeight: 600,
															}}
														/>
													)}
													{!condition.isRequired && (
														<Chip
															label={t('optional')}
															size="small"
															variant="outlined"
															sx={{
																borderColor: theme.palette.grey[400],
																color: 'text.secondary',
															}}
														/>
													)}
												</Stack>
											);
										})}
									</Stack>
								</Box>
							)}
						</Paper>
					</Grid>
				)}

				{/* Responsible Entity */}
				<Grid item xs={12}>
					<FormControl error={Boolean(errors.responsibleEntityId)} fullWidth required>
						<InputLabel>Responsible Entity</InputLabel>
						<Select
							label={t('responsibleEntity')}
							name="responsibleEntityId"
							onChange={handleChange}
							startAdornment={
								<InputAdornment position="start">
									<IconEntity color="action" />
								</InputAdornment>
							}
							value={values.responsibleEntityId}>
							{entities.map((entity) => (
								<MenuItem key={entity.id} value={entity.id}>
									<Box>
										<Typography variant="body2">{entity.name}</Typography>
										<Typography variant="body2" color="text.secondary">
											{entity.type}
										</Typography>
									</Box>
								</MenuItem>
							))}
						</Select>
						{errors.responsibleEntityId && (
							<FormHelperText>{errors.responsibleEntityId}</FormHelperText>
						)}
					</FormControl>
				</Grid>

				{/* Classification Fields */}
				<Grid item xs={12} md={4}>
					<FormControl fullWidth>
						<InputLabel id="component-label">Component</InputLabel>
						<Select
							label={t('component')}
							labelId="component-label"
							name="componentId"
							onChange={handleChange}
							value={values.componentId}>
							<MenuItem value="">
								<em>None</em>
							</MenuItem>
							{components.map((component) => (
								<MenuItem key={component.id} value={component.id}>
									{component.name}
								</MenuItem>
							))}
						</Select>
						<FormHelperText>
							{components.length === 0
								? t('spendPlan.activities.noComponents')
								: t('spendPlan.activities.componentOptional')}
						</FormHelperText>
					</FormControl>
				</Grid>

				<Grid item xs={12} md={4}>
					<FormControl fullWidth>
						<InputLabel id="category-label">Category</InputLabel>
						<Select
							label={t('category')}
							labelId="category-label"
							name="categoryId"
							onChange={handleChange}
							value={values.categoryId}>
							<MenuItem value="">
								<em>None</em>
							</MenuItem>
							{categories.map((category) => (
								<MenuItem key={category.id} value={category.id}>
									{category.name}
								</MenuItem>
							))}
						</Select>
						<FormHelperText>
							{categories.length === 0
								? t('spendPlan.activities.noCategories')
								: t('spendPlan.activities.categoryOptional')}
						</FormHelperText>
					</FormControl>
				</Grid>

				<Grid item xs={12} md={4}>
					<FormControl fullWidth>
						<InputLabel id="expense-type-label">Expense Type</InputLabel>
						<Select
							label={t('expenseType')}
							labelId="expense-type-label"
							name="expenseTypeId"
							onChange={handleChange}
							value={values.expenseTypeId}>
							<MenuItem value="">
								<em>None</em>
							</MenuItem>
							{expenseTypes.map((expenseType) => (
								<MenuItem key={expenseType.id} value={expenseType.id}>
									{expenseType.name}
								</MenuItem>
							))}
						</Select>
						<FormHelperText>
							{expenseTypes.length === 0
								? t('spendPlan.activities.noExpenseTypes')
								: 'Expense type (optional)'}
						</FormHelperText>
					</FormControl>
				</Grid>

				{/* Contract Selection */}
				<Grid item xs={12}>
					<FormControl fullWidth>
						<InputLabel id="contract-label">Contract</InputLabel>
						<Select
							label={t('contract')}
							labelId="contract-label"
							name="contractId"
							onChange={handleChange}
							startAdornment={
								<InputAdornment position="start">
									<IconContract color="action" />
								</InputAdornment>
							}
							value={values.contractId}>
							<MenuItem value="">
								<em>None</em>
							</MenuItem>
							{v1Contracts.map((contract) => {
								const supplierName = getV1ContractSupplierName(contract);
								const contractAmount = getV1ContractAmountForCurrency(contract, reportingCurrency);
								return (
									<MenuItem key={contract.id} value={contract.id}>
										<Box>
											<Typography variant="body2" sx={{ fontWeight: 600 }}>
												{contract.num}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												{supplierName} •{' '}
												{formatCurrency({
													value: contractAmount.amount,
													currency: contractAmount.currency,
												})}
											</Typography>
										</Box>
									</MenuItem>
								);
							})}
						</Select>
						<FormHelperText>
							{v1Contracts.length === 0
								? 'No contracts available'
								: t('spendPlan.activities.contractOptional')}
						</FormHelperText>
					</FormControl>
				</Grid>

				{/* Commitment Amount (shown when contract selected) */}
				{values.contractId && (
					<Grid item xs={12} md={6}>
						<TextField
							fullWidth
							helperText={t('spendPlan.activities.commitmentAmountHelper')}
							InputProps={{
								endAdornment: <InputAdornment position="end">{reportingCurrency}</InputAdornment>,
							}}
							label={t('spendPlan.activities.commitmentAmount')}
							name="commitmentAmount"
							onChange={handleChange}
							type="number"
							value={values.commitmentAmount}
						/>
					</Grid>
				)}

				{/* Commitment Amount — Local Currency (nominal, no FX conversion) */}
				{values.contractId && localCurrency && (
					<Grid item xs={12} md={6}>
						<TextField
							fullWidth
							helperText={t('spendPlan.activities.commitmentAmountLocalHelper')}
							InputProps={{
								endAdornment: <InputAdornment position="end">{localCurrency}</InputAdornment>,
							}}
							label={t('spendPlan.activities.commitmentAmountLocal')}
							name="commitmentAmountLocal"
							onChange={handleChange}
							type="number"
							value={values.commitmentAmountLocal}
						/>
					</Grid>
				)}

				{/* Custom Activity Columns */}
				<CustomFieldsFormSection
					columns={
						(project?.customFieldDefinitions || []).find((d) => d.entityType === 'activity')
							?.definitions || []
					}
					customData={values.customData}
					onChange={handleCustomFieldChange}
					title="Custom Activity Fields"
				/>

				{/* Planned Amount (Reporting Currency) */}
				<Grid item xs={12} md={6}>
					<TextField
						error={Boolean(errors.plannedAmount)}
						fullWidth
						helperText={errors.plannedAmount}
						InputProps={{
							endAdornment: <InputAdornment position="end">{reportingCurrency}</InputAdornment>,
						}}
						label={t('plannedAmount')}
						name="plannedAmount"
						onChange={handleChange}
						required
						type="number"
						value={values.plannedAmount}
					/>
				</Grid>

				{/* Local Currency Amount */}
				{localCurrency && (
					<Grid item xs={12} md={6}>
						<TextField
							fullWidth
							InputProps={{
								endAdornment: <InputAdornment position="end">{localCurrency}</InputAdornment>,
							}}
							label={t('localAmount')}
							name="amountLocal"
							onChange={handleChange}
							type="number"
							value={values.amountLocal}
						/>
					</Grid>
				)}

				{/* Timeline Selection - Optional with checkbox toggle */}
				<Grid item xs={12} sx={{ mt: -1 }}>
					<Box
						onClick={() => {
							if (showTimeline) {
								// Clear date values when unchecking
								setValues((prev) => ({
									...prev,
									endDate: '',
									startDate: '',
									timelinePreset: '',
								}));
							}
							setShowTimeline((prev) => !prev);
						}}
						sx={{
							alignItems: 'center',
							border: '1px solid',
							borderColor: showTimeline ? 'primary.main' : 'divider',
							borderRadius: 1,
							cursor: 'pointer',
							display: 'flex',
							gap: 1.5,
							p: 1.5,
							transition: 'all 0.2s ease',
							'&:hover': {
								borderColor: showTimeline ? 'primary.main' : 'text.secondary',
							},
						}}>
						<Checkbox checked={showTimeline} size="small" sx={{ p: 0 }} />
						<IconCalendar color={showTimeline ? 'primary' : 'action'} sx={{ fontSize: 20 }} />
						<Box sx={{ flex: 1 }}>
							<Typography variant="body2" sx={{ fontWeight: 500 }}>
								{t('spendPlan.activities.setTimeline')}
							</Typography>
							<Typography variant="caption" color="text.secondary">
								{showTimeline
									? t('spendPlan.activities.timelineSet')
									: t('spendPlan.activities.timelineOptional')}
							</Typography>
						</Box>
					</Box>
				</Grid>

				{/* Timeline Presets - shown when timeline is enabled */}
				{showTimeline && (
					<Grid item xs={12}>
						<TimelinePresets
							endDate={values.endDate}
							fiscalYearStartMonth={parseFiscalYearStartMonth(project?.fiscalYearStart)}
							onChange={(dates) => {
								setValues((prev) => ({
									...prev,
									endDate: dates.endDate,
									startDate: dates.startDate,
									timelinePreset: dates.presetId,
								}));
							}}
							selectedPreset={values.timelinePreset}
							startDate={values.startDate}
						/>
					</Grid>
				)}
			</Grid>

			{error && (
				<Alert ref={errorRef} severity="error" sx={{ mt: 2 }}>
					{error}
				</Alert>
			)}
		</form>
	);
};
